import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useDynamicModalContext } from "@umami/components";
import * as Auth from "@umami/social-auth";
import {
  useAsyncActionHandler,
  useCurrentAccount,
  useSetupSaplingAccountsForExistingWallets,
} from "@umami/state";
import { FormProvider, useForm } from "react-hook-form";

import { usePasskey } from "../../hooks/usePasskey";
import { store } from "../../utils/store";
import { LoginButton } from "../../views/SessionLogin/LoginButton";
import { PasswordInput } from "../PasswordInput";

export const ActivateSaplingModal = () => {
  const { onClose } = useDynamicModalContext();
  const currentAccount = useCurrentAccount();
  const setupSaplingAccountsForExistingWallets = useSetupSaplingAccountsForExistingWallets();
  const { handleAsyncAction, isLoading } = useAsyncActionHandler();
  const {
    authenticate,
    isPasskeyFlow,
    getIsPasskeyAccount,
    isLoading: isPasskeyLoading,
    isPasskeyUser,
  } = usePasskey();

  const form = useForm({ mode: "onBlur", defaultValues: { password: "" } });

  const isPasskeyAccount =
    isPasskeyFlow && getIsPasskeyAccount(currentAccount || null) && isPasskeyUser;
  const isUnlockDisabled = !isPasskeyAccount && !form.formState.isValid;

  const onSubmit = () =>
    handleAsyncAction(async () => {
      if (!currentAccount) {
        return;
      }

      if (currentAccount.type === "social") {
        const { secretKey } = await Auth.forIDP(currentAccount.idp).getCredentials();

        await setupSaplingAccountsForExistingWallets({
          accountType: "social",
          secretKey,
          accountPkh: currentAccount.address.pkh,
          getState: () => store.getState(),
        });
      } else if (isPasskeyAccount) {
        const { dek } = await authenticate();

        await setupSaplingAccountsForExistingWallets({
          accountType: currentAccount.type as "mnemonic" | "secret_key",
          password: dek,
          getState: () => store.getState(),
        });
      } else {
        const password = form.getValues().password;

        await setupSaplingAccountsForExistingWallets({
          accountType: currentAccount.type as "mnemonic" | "secret_key",
          password,
          getState: () => store.getState(),
        });
      }

      onClose();
    });

  return (
    <Modal isCentered isOpen onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Activate Shielded Transactions</ModalHeader>
        <ModalCloseButton />
        <ModalBody paddingBottom="24px">
          <Text marginBottom="24px">
            To use Sapling features, you need to activate it for your account first.
          </Text>
          <FormProvider {...form}>
            {currentAccount && (
              <VStack spacing="24px">
                {currentAccount.type === "social" ? (
                  <LoginButton
                    idp={currentAccount.idp}
                    isLoading={isLoading}
                    onSubmit={onSubmit}
                    prefix="Sign in with"
                  />
                ) : (
                  <>
                    {!isPasskeyAccount && <PasswordInput inputName="password" />}
                    <Button
                      width="full"
                      isDisabled={isUnlockDisabled}
                      isLoading={isLoading || isPasskeyLoading}
                      onClick={onSubmit}
                      size="lg"
                      variant="primary"
                    >
                      Activate
                    </Button>
                  </>
                )}
              </VStack>
            )}
          </FormProvider>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
